import { z } from "zod";
import checkFormStateByZod, { IZodErrorsState } from "../utils/checkFormStateByZod";
import { IEditUser, IEditUserPassword, IUser, IUserRequest, TUserRequest } from "../model/IUser";
import { useTypedSelector } from "./useTypedSelector";
import { TUserState, getCurrentUser } from "../store/authSlice";
import { IRole, ROLES, TRole } from "../model/IRole";
import { userApi } from "../api/user.api";
import api, { getErrorFromCatch, serializeFieldErrors } from "../api/api";
import { msg } from "../msg";
import toast from "react-hot-toast";
import { Option } from "react-multi-select-component";
import { useTypedDispatch } from "./useTypedDispatch";
import { useAccess } from "./useAccess";
import { ITicketRoleFull } from "../model/ITicketRole";

export function useUser() {
  const currentUser = useTypedSelector(getCurrentUser());
  const dispatch = useTypedDispatch();

  // -- Check permissions
  const { getSettingAccess } = useAccess();

  // --
  // -- Data checking scheme
  // --
  const newUserRequestSchema = z.object({
    email: z
      .string()
      .email(msg('user-email-unsuitable'))
      .min(1, msg('user-no-email')),
    password: z
      .string()
      .min(4, msg('user-short-password')),
    passwordConfirm: z
      .string()
      .min(4, msg('user-short-password')),
    roles: z
      .string()
      .min(3, msg('user-no-roles')),
  } as Record<keyof IUserRequest, any>)
    .superRefine(({ passwordConfirm, password }, ctx) => {
      if (passwordConfirm !== password) {
        ctx.addIssue({
          code: "custom",
          message: msg('user-password-no-match'),
          path: ['passwordConfirm'],
        });
      }
    });

  // -- scheme to update main user information by admin
  const editUserMainRequestSchema = z.object({});

  // -- scheme to update passwords user by admin
  const editUserPassRequestSchema = z.object({
    password: z
      .string()
      .min(4, msg('user-short-password')),
    passwordConfirm: z
      .string()
      .min(4, msg('user-short-password')),
  } as Record<keyof IUserRequest, any>)
    .superRefine(({ passwordConfirm, password }, ctx) => {
      if (passwordConfirm !== password) {
        ctx.addIssue({
          code: "custom",
          message: msg('user-password-no-match'),
          path: ['passwordConfirm'],
        });
      }
    });

  // -- scheme to update current user information
  const editUserRequestSchema = z.object({
    password: z.string(),
    passwordConfirm: z.string(),
  } as Record<keyof IEditUserPassword, any>)
    .superRefine(({ password, passwordConfirm }, ctx) => {
      // - there isn't a new password
      if (!passwordConfirm && !password) return;

      // - there isn't the current password
      // !currentPassword && ctx.addIssue({
      //     code: "custom",
      //     message: msg('user-no-current-password'), // no current
      //     path: ['currentPassword'],
      //   });

      // - the passwords is too short
      // currentPassword.length < 4 && ctx.addIssue({
      //     code: "custom",
      //     message: msg('user-short-password'),
      //     path: ['currentPassword'],
      //   });
      password.length < 4 && ctx.addIssue({
        code: "custom",
        message: msg('user-short-password'),
        path: ['password'],
      });
      passwordConfirm.length < 4 && ctx.addIssue({
        code: "custom",
        message: msg('user-short-password'),
        path: ['passwordConfirm'],
      });

      if (passwordConfirm !== password) {
        ctx.addIssue({
          code: "custom",
          message: msg('user-password-no-match'),
          path: ['passwordConfirm'],
        });
      }
    });

  // --
  // -- Data Getting Methods
  // --
  const [tryToAddUser] = userApi.useAddUserMutation();
  const [tryToDeleteUser] = userApi.useDeleteUserMutation();
  const [tryToEditUser] = userApi.useEditUserMutation();
  const [tryToEditOptionsUser] = userApi.useEditUserOptionsMutation();

  // -- 
  // -- Methods
  // -- 
  const convertRolesToIdsStr = (roles?: IRole[]): string => (roles || [])
    .map(({ role }) => role)
    .join(',');

  const convertVendorFullRolesToRolesStr = (vFullRoles?: ITicketRoleFull[]): string => (vFullRoles || [])
    .map(({ ticketRole }) => ticketRole?.id)
    .join(',');

  const convertAgentFullRolesToRolesStr = (vFullRoles?: ITicketRoleFull[]): string => (vFullRoles || [])
    .map(({ ticketRole }) => ticketRole?.id)
    .join(',');

  const convertOptionRolesToIdsStr = (roleOptions?: Option[]): string => (roleOptions || [])
    .map(({ value }) => value)
    .join(',');

  const convertRolesIdsToRoles = (rolesIdsStr: string): Option[] => rolesIdsStr
    .split(',')
    .reduce((acc, roleId) => [
      ...acc,
      ...(ROLES?.[roleId as TRole]
        ? [{ label: ROLES[roleId as TRole], value: roleId }]
        : []
      )
    ], [] as Option[]) || [];


  const convertIdsToUsers = (ids?: string | number, users?: IUser[]): IUser[] => (String(ids) || '')
    .split(',')
    .map((id) => (users || []).find(({ id: originalId }) => originalId === id))
    .filter((user) => !!user) as IUser[];

  // -- 
  // -- Get User Roles
  // -- 
  const getUserRoles = (user: TUserState = currentUser): TRole[] => (user?.userRole || [])
    .map(({ role }) => role);

  // -- 
  // -- Check if user has one of roles
  // -- 
  const hasRoles = (roles: TRole[], user: TUserState = currentUser): boolean =>
    (new Set([...getUserRoles(user), ...roles])).size !== [...getUserRoles(user), ...roles].length;

  // --
  // -- Returns string = API error
  // -- False - OK
  // --
  const addUser = async (userState: IUserRequest): Promise<IZodErrorsState<IUserRequest> | null> => {
    // 1. Check data
    const errs = checkFormStateByZod(newUserRequestSchema, userState);
    if (errs && Object.values(errs)?.length) return errs;

    const { vendor, vendorRole, agentRole, ...state } = userState;

    // -- Preparing of vendor information
    const vendorRoles = vendorRole && vendor
      ? (vendorRole.trim().split(',') || []).map((vRole) => ({
        vendorId: Number(vendor),
        vendorRoleId: Number(vRole),
      }))
      : [];

    // -- Preparing of agent information
    const agentRoles = agentRole
      ? (agentRole.trim().split(',') || []).map((vRole) => ({
        agentRoleId: Number(vRole),
      }))
      : [];

    // - 2. Send data
    try {
      await tryToAddUser({
        ...state,
        vendorRoles,
        agentRoles,
      }).unwrap();
      return null;
    } catch (err) {
      toast.error(`${msg('user-create-failed')} \n${getErrorFromCatch(err)}`)
      return { email: getErrorFromCatch(err) }
    }
  };

  // --
  // -- Returns string = API error
  // -- null - OK
  // --
  const deleteUser = async (userId: string): Promise<string | null> => {

    // 1. Check data
    if (!userId) return msg('not-enough-data');

    // - 2. Send data
    try {
      await tryToDeleteUser(userId).unwrap();;
      return null;
    } catch (err) {
      toast.error(`${msg('user-delete-failed')} \n${getErrorFromCatch(err)}`)
      return getErrorFromCatch(err);
    }
  };

  // --
  // -- Returns string = API error
  // -- False - OK
  // --
  const editMainUserInfo = async (id: string, userState: IEditUser): Promise<IZodErrorsState<IEditUser> | null> => {
    // 1. Check data
    const errs = checkFormStateByZod(editUserMainRequestSchema, userState);
    if (errs && Object.values(errs)?.length) return errs;

    const { vendor, vendorRole, agentRole, ...state } = userState;
    const vendorRoles = vendorRole && vendor
      ? (vendorRole.trim().split(',') || []).map((vRole) => ({
        vendorId: Number(vendor),
        vendorRoleId: Number(vRole),
      }))
      : [];

    // -- Preparing of agent information
    const agentRoles = agentRole
      ? (agentRole.trim().split(',') || []).map((vRole) => ({
        agentRoleId: Number(vRole),
      }))
      : [];

    // - 2. Send data
    try {
      await tryToEditUser({
        id,
        ...state,
        vendorRoles,
        agentRoles,
      }).unwrap();

      // id === currentUser?.id && dispatch(api.util.invalidateTags(['Me']));
      return null;
    } catch (err) {
      toast.error(`${msg('user-edit-failed')} \n${getErrorFromCatch(err)}`)
      return { name: getErrorFromCatch(err) }
    }
  };


  // --
  // -- Returns string = API error
  // -- False - OK
  // --
  const editOptionsUserInfo = async (id: string, isAllowedForTickets?: boolean, isSearchableSales?: boolean, isSearchableTickets?: boolean): Promise<null | IUser> => {
    // 1. Check data
    if (!getSettingAccess('edit-user') && currentUser?.id !== id) {
      toast.error(`${msg('not-permissions-to-assignable')}`);
      return null;
    }

    // - 2. Send data
    try {
      const updatedUser = await tryToEditOptionsUser({ id, isAllowedForTickets, isSearchableSales, isSearchableTickets }).unwrap();
      return updatedUser;
    } catch (err) {
      toast.error(`${msg('user-edit-failed')} \n${getErrorFromCatch(err)}`)
      return null;
    }
  };

  const editPassUserInfo = async (id: string, passwordState: IEditUserPassword): Promise<IZodErrorsState<IEditUserPassword> | null> => {
    // 1. Check data
    const errs = checkFormStateByZod(editUserPassRequestSchema, passwordState);
    if (errs && Object.values(errs)?.length) return errs;

    // -- If editable user is equal the current user - check current password
    if (currentUser?.id === id && (passwordState.currentPassword || '').trim().length < 3)
      return { currentPassword: 'Your current password is required!' }

    // - 2. Send data
    try {
      await tryToEditUser({
        id,
        password: passwordState.password?.trim(),
        passwordConfirm: passwordState.passwordConfirm?.trim(),
        currentPassword: passwordState.currentPassword?.trim() || undefined,
      }).unwrap();
      return null;
    } catch (err) {
      toast.error(`${msg('user-edit-failed')} \n${getErrorFromCatch(err)}`)
      return serializeFieldErrors(err);
      // return { password: getErrorFromCatch(err) }
    }
  };

  // --
  // -- Returns string = API error
  // -- False - OK
  // --
  const editUserInfo = async (id: string, userState: IEditUser): Promise<IZodErrorsState<IEditUser> | string | false> => {
    // 1. Check data
    const errs = checkFormStateByZod(editUserRequestSchema, userState);
    if (errs && Object.values(errs)?.length) return errs;

    // - 2. Send data
    try {
      await tryToEditUser({ id, ...userState }).unwrap();
      return false;
    } catch (err) {
      return getErrorFromCatch(err);
    }
  };

  return {
    addUser,
    deleteUser,
    editMainUserInfo, // - for admin flow
    editPassUserInfo, // - for admin flow
    editUserInfo, // - for user flow
    convertIdsToUsers,

    hasRoles,
    getUserRoles,

    convertOptionRolesToIdsStr,
    convertRolesIdsToRoles,
    convertRolesToIdsStr,
    convertVendorFullRolesToRolesStr,
    convertAgentFullRolesToRolesStr,
    editOptionsUserInfo,
  };
}